import { uniqueId } from '@aipb/shared/util/other';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output
} from '@angular/core';
import { Overlay, View } from 'ol';
import { Control } from 'ol/control';
import { Interaction } from 'ol/interaction';
import BaseLayer from 'ol/layer/Base';
import Map from 'ol/Map';
import MapBrowserEvent from 'ol/MapBrowserEvent';

@Component({
  selector: 'aipb-ol-map',
  templateUrl: './ol-map.component.html',
  styleUrls: ['./ol-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OlMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() layers: BaseLayer[] = [];
  @Input() controls: Control[] = [];
  @Input() view: View = null;
  @Input() interactions: Interaction[] = [];
  @Input() overlays: Overlay[] = [];

  @Output() mapReady = new EventEmitter<Map>();
  @Output() mapClick = new EventEmitter<MapBrowserEvent<never>>();
  @Output() mapContextMenu = new EventEmitter<MouseEvent>();
  @Output() mapMoveEnd = new EventEmitter<MapBrowserEvent<never>>();

  private _map: Map;

  constructor(private zone: NgZone) {}

  private _id: string = 'ol-map-' + uniqueId();

  get id(): string {
    return this._id;
  }

  ngAfterViewInit(): void {
    this.zone.runOutsideAngular(() => this.initMap());
  }

  ngOnChanges(): void {
    this.zone.runOutsideAngular(() => this.initMap());
  }

  ngOnDestroy() {
    this.destroyMap();
  }

  private initMap(): void {
    if (this._map) {
      this.destroyMap();
    }

    this._map = new Map({
      view: this.view,
      layers: this.layers,
      controls: this.controls,
      interactions: this.interactions,
      overlays: this.overlays,
      target: this._id
    });

    this._map.getViewport().addEventListener('contextmenu', (evt) => {
      this.mapContextMenu.emit(evt);
      return false;
    });

    this._map.on('click', (evt: MapBrowserEvent<never>) => {
      this.mapClick.emit(evt);
    });

    this._map.on('moveend', (evt: MapBrowserEvent<never>) => {
      this.mapMoveEnd.emit(evt);
    });

    this._map.on('loadstart', () => {
      this._map.getTargetElement().classList.add('ol-map-spinner');
    });

    this._map.on('loadend', () => {
      this._map.getTargetElement().classList.remove('ol-map-spinner');
    });

    this.mapReady.emit(this._map);
  }

  private destroyMap(): void {
    this._map.setTarget(null);
    this._map.dispose();
    this._map = null;
  }
}
